import { DELETE_LISTING, UPDATE_LISTING } from '@paperstac/common/lib/serverDispatchActionTypes';
import serverDispatch from '@paperstac/common/lib/services/serverDispatch';
import Box from '@paperstac/ui/lib/Box';
import ConfirmPopover from '@paperstac/ui/lib/ConfirmPopover';
import Flex from '@paperstac/ui/lib/Flex';
import ExternalLinkIcon from '@paperstac/ui/lib/icons/ExternalLinkIcon';
import PencilIcon from '@paperstac/ui/lib/icons/PencilIcon';
import XIcon from '@paperstac/ui/lib/icons/XIcon';
import Link from '@paperstac/ui/lib/Link';
import LinkButton from '@paperstac/ui/lib/LinkButton';
import Spinner from '@paperstac/ui/lib/Spinner';
import UnstyledButton from '@paperstac/ui/lib/UnstyledButton';
import React from 'react';
import { LISTING_STATUS, MyListingsPageContext } from './MyListingsPageProvider';

const ListingHeader = React.memo(({ listingId, status, url, ...props }) => {
  const { setManageListingId, showVerify, verificationRequiredToActivate } = React.useContext(MyListingsPageContext);
  const [deleteConfirmIsVisible, setDeleteConfirmIsVisible] = React.useState(false);
  const showDeleteConfirm = React.useCallback(() => setDeleteConfirmIsVisible(true), []);
  const hideDeleteConfirm = React.useCallback(() => setDeleteConfirmIsVisible(false), []);
  const [isDeleting, setDeleting] = React.useState(false);
  const [isSubmitting, setSubmitting] = React.useState(false);

  const handleEdit = React.useCallback(() => setManageListingId(listingId), [listingId, setManageListingId]);

  const handleDelete = React.useCallback(() => {
    if (isDeleting) return;
    setDeleting(true);
    serverDispatch({ action: DELETE_LISTING, payload: { listingId } }).catch((err) => {
      alert(err.message);
      setDeleting(false);
    });
  }, [isDeleting, listingId]);

  const handleUpdate = React.useCallback(
    (updates) => {
      if (isSubmitting) return;
      setSubmitting(true);
      serverDispatch({ action: UPDATE_LISTING, payload: { ...updates, id: listingId } }).catch((err) => {
        alert(err.message);
        setSubmitting(false);
      });
    },
    [isSubmitting, listingId]
  );

  const handleActivate = React.useCallback(
    () => (verificationRequiredToActivate ? showVerify() : handleUpdate({ isActive: true })),
    [handleUpdate, showVerify, verificationRequiredToActivate]
  );
  const handleDeactivate = React.useCallback(() => handleUpdate({ isActive: false }), [handleUpdate]);

  React.useEffect(() => setSubmitting(false), [status]);

  return (
    <Box
      tx="listingHeader"
      variant={
        isSubmitting
          ? 'submitting'
          : status === LISTING_STATUS.ACTIVE
            ? 'active'
            : status === LISTING_STATUS.INACTIVE
              ? 'inactive'
              : 'attention'
      }
      sx={{
        color: 'gray.9',
        borderTopLeftRadius: 'corner',
        borderTopRightRadius: 'corner',
      }}
      {...props}
    >
      {isSubmitting && (
        <Flex alignItems="center">
          <Spinner size="tiny" />
          <Box ml={1}>Submitting...</Box>
        </Flex>
      )}
      {!isSubmitting && (
        <Flex alignItems="center">
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: 'circle',
              bg: status === LISTING_STATUS.ACTIVE ? 'green' : status === LISTING_STATUS.INACTIVE ? 'gray.6' : 'red',
              mr: '6px',
            }}
          />
          <Box flexGrow={1}>
            {status}
            {status === LISTING_STATUS.ACTIVE && (
              <Box sx={{ color: 'gray.6', display: 'inline', ml: 2 }}>
                (
                <LinkButton onClick={handleDeactivate} target="_blank">
                  Deactivate
                </LinkButton>
                ,{' '}
                <Link href={url} target="_blank">
                  View{' '}
                  <ExternalLinkIcon
                    size={14}
                    sx={{
                      position: 'relative',
                      top: '-2px',
                    }}
                  />
                </Link>
                )
              </Box>
            )}
            {status === LISTING_STATUS.INACTIVE && (
              <Box sx={{ color: 'gray.6', display: 'inline', ml: 2 }}>
                (
                <LinkButton onClick={handleActivate} target="_blank">
                  Activate
                </LinkButton>
                ,{' '}
                <Link href={url} target="_blank">
                  Preview{' '}
                  <ExternalLinkIcon
                    size={14}
                    sx={{
                      position: 'relative',
                      top: '-2px',
                    }}
                  />
                </Link>
                )
              </Box>
            )}
          </Box>
          <Box>
            <UnstyledButton
              onClick={handleEdit}
              mr={3}
              sx={{
                mr: 3,
                color: 'gray.7',
                ':hover,:focus': { color: 'blue' },
              }}
            >
              <PencilIcon />
            </UnstyledButton>

            <ConfirmPopover
              isOpen={deleteConfirmIsVisible}
              onConfirm={handleDelete}
              onCancel={hideDeleteConfirm}
              text="Are you sure you want to delete this?"
              confirmText="Delete"
            >
              <UnstyledButton
                onClick={showDeleteConfirm}
                sx={{
                  color: 'gray.7',
                  ':hover,:focus': { color: 'red' },
                }}
              >
                <XIcon size={14} />
              </UnstyledButton>
            </ConfirmPopover>
          </Box>
        </Flex>
      )}
    </Box>
  );
});

ListingHeader.propTypes = {};

ListingHeader.defaultProps = {};

ListingHeader.displayName = 'ListingHeader';

export default ListingHeader;
